import { useCallback, useState } from "react";
import styled from "@emotion/styled";
import type { FC, KeyboardEvent, MouseEvent } from "react";
import {
  MDSIcon,
  MDSIconColor,
  MDSModalDialog,
  MDSButton,
  MDSButtonSlotName,
  MDSButtonGroup,
  MDSButtonVariant,
  MDSText,
  MDSTextVariant,
  MDSDialogTextContent,
  MDSTextField,
} from "@/mds";
import { MDSIconKind } from "@/mds/components/icon";
import { MDSDialogSlotName } from "@/mds/components/overlays/dialog/dialog.types";
import { api } from "@/modules/api";
import { logger } from "@/modules/logger";
import { trackEvent, TrackedEvent } from "@/domains/metrics";
import { useHeadsUpBaseContextPayload } from "@/components/heads-up/analytics";

type FeedbackResponse = "UPVOTE" | "DOWNVOTE";

type Props = {
  smartCardId: string;
  entryId: string;
  noteId: string;
  noteTitle: string;
  topic: string;
  insight: string | null;
  hasQuote: boolean;
  recordingOffsetSeconds: number | null;
};

const postLiveEntryFeedback = async ({
  smartCardId,
  entryId,
  response,
  comment,
}: {
  smartCardId: string;
  entryId: string;
  response: FeedbackResponse;
  comment: string | null;
}): Promise<void> => {
  const result = await (
    api.post as unknown as (
      url: string,
      options: {
        params: { path: { smart_card_id: string } };
        body: { entry_id: string; response: string; comment: string | null };
      }
    ) => Promise<{ data: unknown }>
  )(`/v2/smart-views/{smart_card_id}/live-entry-feedback`, {
    params: { path: { smart_card_id: smartCardId } },
    body: { entry_id: entryId, response, comment },
  });

  if (!result.data) {
    throw new Error("Failed to submit live entry feedback");
  }
};

export const LiveEntryFeedback: FC<Props> = ({
  smartCardId,
  entryId,
  noteId,
  noteTitle,
  topic,
  insight,
  hasQuote,
  recordingOffsetSeconds,
}) => {
  const [submittedResponse, setSubmittedResponse] = useState<FeedbackResponse | null>(null);
  const [pendingResponse, setPendingResponse] = useState<FeedbackResponse | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const basePayload = useHeadsUpBaseContextPayload();

  const stopProp = useCallback((event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
  }, []);

  const stopHeaderToggleKeyEvent = useCallback((event: KeyboardEvent<HTMLElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.stopPropagation();
    }
  }, []);

  const handleThumbClick = useCallback(
    (event: MouseEvent<HTMLElement>, response: FeedbackResponse) => {
      event.stopPropagation();
      setPendingResponse(response);
      setComment("");
      setErrorMessage(null);
      setIsModalOpen(true);
    },
    []
  );

  const handleClose = useCallback(() => {
    setIsModalOpen(false);
    setPendingResponse(null);
    setComment("");
    setErrorMessage(null);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!pendingResponse) return;
    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      await postLiveEntryFeedback({
        smartCardId,
        entryId,
        response: pendingResponse,
        comment: comment.trim() || null,
      });
      setSubmittedResponse(pendingResponse);
      trackEvent(TrackedEvent.HeadsUpLiveEntryFeedbackSubmit, {
        ...basePayload,
        smart_card_id: smartCardId,
        entry_id: entryId,
        note_id: noteId,
        note_title: noteTitle,
        response: pendingResponse,
        comment: comment.trim() || null,
        topic,
        insight,
        has_quote: hasQuote,
        recording_offset_seconds: recordingOffsetSeconds,
      });
      setIsModalOpen(false);
      setPendingResponse(null);
      setComment("");
    } catch (_err) {
      logger.error({ message: "Failed to submit live entry feedback" });
      setErrorMessage("Could not submit feedback. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }, [
    pendingResponse,
    comment,
    basePayload,
    smartCardId,
    entryId,
    noteId,
    noteTitle,
    topic,
    insight,
    hasQuote,
    recordingOffsetSeconds,
  ]);

  if (submittedResponse) {
    return (
      <StyledFeedbackInline data-live-feedback onClick={stopProp}>
        <MDSIcon
          kind={
            submittedResponse === "UPVOTE" ? MDSIconKind.UpvoteSolid : MDSIconKind.DownvoteSolid
          }
          color={MDSIconColor.Secondary}
          size={10}
          boxSize={12}
        />
        <StyledThanksMessage>Thanks!</StyledThanksMessage>
      </StyledFeedbackInline>
    );
  }

  return (
    <>
      <StyledFeedbackInline data-live-feedback onClick={stopProp}>
        <StyledFeedbackActionButton
          onClick={e => handleThumbClick(e, "UPVOTE")}
          onKeyDown={stopHeaderToggleKeyEvent}
          aria-label="Thumbs up"
        >
          <MDSIcon
            kind={MDSIconKind.UpvoteSolid}
            color={MDSIconColor.Secondary}
            size={10}
            boxSize={12}
          />
        </StyledFeedbackActionButton>
        <StyledFeedbackActionButton
          onClick={e => handleThumbClick(e, "DOWNVOTE")}
          onKeyDown={stopHeaderToggleKeyEvent}
          aria-label="Thumbs down"
        >
          <MDSIcon
            kind={MDSIconKind.DownvoteSolid}
            color={MDSIconColor.Secondary}
            size={10}
            boxSize={12}
          />
        </StyledFeedbackActionButton>
      </StyledFeedbackInline>

      <MDSModalDialog isOpen={isModalOpen} handleClose={handleClose}>
        <MDSText slot={MDSDialogSlotName.Header} variant={MDSTextVariant.HeaderL}>
          Share feedback
        </MDSText>
        <MDSDialogTextContent slot={MDSDialogSlotName.Content}>
          <MDSText variant={MDSTextVariant.ParagraphS}>How useful was this insight?</MDSText>
          <StyledResponseButtons>
            <MDSButton
              variant={
                pendingResponse === "UPVOTE"
                  ? MDSButtonVariant.EmphasisLight
                  : MDSButtonVariant.Default
              }
              onClick={() => setPendingResponse("UPVOTE")}
              aria-pressed={pendingResponse === "UPVOTE"}
            >
              <MDSIcon slot={MDSButtonSlotName.LeftIcon} kind={MDSIconKind.Upvote} />
              Good
            </MDSButton>
            <MDSButton
              variant={
                pendingResponse === "DOWNVOTE"
                  ? MDSButtonVariant.DangerFilled
                  : MDSButtonVariant.Default
              }
              onClick={() => setPendingResponse("DOWNVOTE")}
              aria-pressed={pendingResponse === "DOWNVOTE"}
            >
              <MDSIcon slot={MDSButtonSlotName.LeftIcon} kind={MDSIconKind.Downvote} />
              Bad
            </MDSButton>
          </StyledResponseButtons>
          <MDSText variant={MDSTextVariant.ParagraphS}>
            {pendingResponse === "UPVOTE"
              ? "Glad this insight was helpful! Any additional thoughts?"
              : "Sorry this insight wasn't helpful. What could be improved?"}
          </MDSText>
          <StyledFeedbackTextArea
            value={comment}
            onChange={setComment}
            placeholder="Share more details"
            isTextArea={true}
            textAreaProps={{
              rows: 3,
              onKeyDown: stopHeaderToggleKeyEvent,
            }}
          />
          {errorMessage ? <StyledInlineError role="alert">{errorMessage}</StyledInlineError> : null}
        </MDSDialogTextContent>
        <MDSButtonGroup slot={MDSDialogSlotName.ButtonsRight}>
          <MDSButton onClick={handleClose}>Cancel</MDSButton>
          <MDSButton
            variant={MDSButtonVariant.Contrast}
            isLoading={isSubmitting}
            onClick={handleSubmit}
          >
            Submit
          </MDSButton>
        </MDSButtonGroup>
      </MDSModalDialog>
    </>
  );
};

const StyledFeedbackInline = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
`;

const StyledFeedbackActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.max};
  background: transparent;
  color: inherit;
  cursor: pointer;
  padding: 0;

  &:hover {
    background: ${({ theme }) => theme.colors.background.button.transparent.hover};
  }
`;

const StyledThanksMessage = styled.span`
  font-size: ${({ theme }) => theme.fontSizes["text-xs"]};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const StyledResponseButtons = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing["2"]};
  margin: ${({ theme }) => theme.spacing["2"]} 0;
`;

const StyledFeedbackTextArea = styled(MDSTextField)`
  width: 100%;
  margin-top: ${({ theme }) => theme.spacing["2"]};
  min-height: 72px;
`;

const StyledInlineError = styled.div`
  margin-top: ${({ theme }) => theme.spacing["2"]};
  color: ${({ theme }) => theme.colors.text.danger};
  font-size: ${({ theme }) => theme.fontSizes["text-xs"]};
`;
